import React, { useState,useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaMapMarkerAlt, FaCalendarAlt} from 'react-icons/fa'; // icons
import './css/ticket.css'; 
import { API_URL_BASE } from './config';

function TicketPurchase() {
	const { eventid } = useParams();
	const [eventresult, setEventResult] = useState(null);
	const [loading, setLoading] = useState(true);
	const [quantities, setQuantities] = useState({});
	const [grandTotal, setGrandTotal] = useState(0);
	const [ticketsToPurchase, setTicketToPurchase] = useState([]);
	const [processingfee, setProcessingfee] = useState(0);
	const [ticketNotification, setTicketNotification] = useState(false);
	const [promoCode, setPromoCode] = useState('');
	const [promoStatus, setPromoStatus] = useState(null); // null | 'valid' | 'invalid'
	const [promoMessage, setPromoMessage] = useState('');
	const [discount, setDiscount] = useState(0); // in cents
	const [discountedTotal, setDiscountedTotal] = useState(0); // in cents
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	let processingfee_fixed = 25;
	
	// Trim spaces + remove quotes
	 const eventidparam = eventid?.trim().replace(/["'\W_]+$/g, "");
	
	const languages = [
	  { code: "nl", label: "NL", flag: "🇳🇱" },
	  { code: "en", label: "EN", flag: "🇬🇧" }
	];

	
	useEffect(() => {
	  fetch(API_URL_BASE + '/ticket.json',{
		method: 'POST',
		     headers: {
		       'Content-Type': 'application/json',
		     },
		     body: JSON.stringify({
				fetchEventDetailForPurchase: {
					eventId: eventidparam
		        }
		     }),
		   })
	    .then(res => {
	      if (!res.ok) throw new Error('Event not found');
	      return res.json();
	    })
	    .then(data => {
	        setEventResult(data.eventdetails); // Adjust based on your actual response shape
	    })
	    .catch(err => {
	      console.error('Error loading event:', err);
	      setEventResult(null);
	    })
	    .finally(() => setLoading(false));
	}, [eventidparam]);
	
	
	useEffect(() => {
		if (!eventresult?.tickets || Object.keys(quantities).length === 0) return;
		
		// Processing fee per ticket (free events have no fee)
		const feePerTicket = (eventresult?.event?.type === "FREE") ? 0 : processingfee_fixed;

		
		// Subtotal: sum of (qty × price) per ticket type
		const subtotal = eventresult.tickets.reduce((acc, ticket) => {
		  const quantity = quantities[ticket.ticketid] || 0;
		  return acc + quantity * ticket.price;
		}, 0);
		
		// Total processing fee only for selected tickets with price > 0
		const totalFee = eventresult.tickets.reduce((acc, ticket) => {
		  const quantity = quantities[ticket.ticketid] || 0;
		  return acc + (ticket.price > 0 ? feePerTicket * quantity : 0);
		}, 0);

		// Grand total
		const total = subtotal + totalFee;

		setProcessingfee(totalFee);
		setGrandTotal(total);
		setDiscountedTotal(Math.max(0, total - discount));
	
		const ticketsToPurchase = eventresult.tickets
		  .filter(ticket => (quantities[ticket.ticketid] || 0) > 0)
		  .map(ticket => ({
		    ticketid: ticket.ticketid,
		    ticketsku: ticket.sku,
		    ticketsold: quantities[ticket.ticketid] || 0
		  }));
		setTicketToPurchase(ticketsToPurchase);  
	  
	}, [quantities, eventresult]);
	
	// Build tickets array with ticketId + quantity	
	const lowStock = eventresult?.tickets?.some(ticket => {
	  const available = ticket.capacity - ticket.ticketsold;
	  return available >= 1 && available <= 5;
	});
	
	//Check for the past events
	const eventEnded =
	  eventresult?.event?.enddatetime &&
	  Date.now() > eventresult.event.enddatetime;
	  
	useEffect(() => {
	  if (!ticketNotification && lowStock) {
	    setTicketNotification(true);
	  }
	}, [lowStock, ticketNotification]);

	
	const MAX_TOTAL_TICKETS = 5;
	const totalSelected = Object.values(quantities).reduce((sum, q) => sum + q, 0);

	const handleCountChange = (ticketId, value) => {
	  const requested = Math.max(0, Number(value));
	  const otherTotal = Object.entries(quantities)
	    .filter(([id]) => id !== String(ticketId))
	    .reduce((sum, [, q]) => sum + q, 0);
	  if (otherTotal + requested > MAX_TOTAL_TICKETS) return;
	  setQuantities((prev) => ({
	    ...prev,
	    [ticketId]: requested
	  }));
	};
	

	const handleApplyPromo = async () => {
		if (!promoCode.trim()) return;
		try {
			const response = await fetch(API_URL_BASE + '/ticket.json', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					validatepromo: {
						eventid: eventidparam,
						promocode: promoCode.trim().toUpperCase()
					}
				})
			});
			const data = await response.json();
			if (data?.promo?.valid) {
				const d = data.promo.discountamount || 0; // cents
				setDiscount(d);
				setDiscountedTotal(Math.max(0, grandTotal - d));
				setPromoStatus('valid');
				setPromoMessage(data.promo.description || `Discount of €${(d / 100).toFixed(2)} applied!`);
			} else {
				setDiscount(0);
				setDiscountedTotal(grandTotal);
				setPromoStatus('invalid');
				setPromoMessage(data?.promo?.message || 'Invalid or expired promo code.');
			}
		} catch {
			setPromoStatus('invalid');
			setPromoMessage('Could not validate promo code. Please try again.');
		}
	};

	const handleRemovePromo = () => {
		setPromoCode('');
		setPromoStatus(null);
		setPromoMessage('');
		setDiscount(0);
		setDiscountedTotal(grandTotal);
	};

	const handleSubmit = () => {
		navigate(`/buy/ticketcheckout/${eventidparam}`,		{
		    state: {
		      event: eventresult?.event,
		      tickets: ticketsToPurchase,
			  total: discountedTotal || grandTotal,
		      processingfee,
			  promoCode: promoStatus === 'valid' ? promoCode.trim().toUpperCase() : null,
			  discount,
			  }
		    });
	};


  return (
	<div>
	<div className="ticket-purchase">
    <div className="ticket-container">
	{!loading && eventresult ? (
		<>	
		<div className="event-header">
		  <div className="event-text">	
			  <h1>{eventresult.event.name}</h1>
			  <p className="event-description">{eventresult.event.description}</p>
			  <div className="event-details">
			    <p><FaCalendarAlt/> <strong>Start:</strong> {new Date(eventresult.event.startdatetime).toLocaleString()}</p>
			    <p><FaCalendarAlt/> <strong>{t("end")}:</strong> {new Date(eventresult.event.enddatetime).toLocaleString()}</p>
			    <p><FaMapMarkerAlt/> <strong>Address:</strong> {eventresult.event.address}</p>
			  </div>
		  </div>
		  <div className="purchase-event-image">
		    <img
		      src={`data:image/png;base64,${eventresult.event.image}`}
		      alt=""
		    />
		  </div>		  
		</div>
		

		{eventEnded ? (
		<div className="event-notification blink">
			<p> Sorry this event has ended  !!! </p>
		</div>
		) : null }
	
		{ (!eventEnded && ticketNotification) ? (
		<div className="ticket-notification">
			<p> Few Tickets left !!! </p>
		</div>
		) : null}

		
		<div className="tickets-list">
		{eventresult.tickets.map((ticket) => {
			const quantity = quantities[ticket.ticketid] || 0;
			const isFree = eventresult?.event?.type === "FREE";
			const feePerTicket = isFree ? 0 : processingfee_fixed;
			const lineTotal = (ticket.price + (ticket.price  > 0 ? feePerTicket : 0)) / 100 * quantity;
			const otherTotal = Object.entries(quantities)
			  .filter(([id]) => id !== String(ticket.ticketid))
			  .reduce((sum, [, q]) => sum + q, 0);
			const maxAllowed = Math.min(MAX_TOTAL_TICKETS - otherTotal, ticket.capacity - ticket.ticketsold);

			return (
					<div key={ticket.ticketid} className="ticket-card">
					       <div className="ticket-info">
							   <div className="ticket-name-price">
							     <div className="ticket-name">{ticket.name}</div>
							     <div className="ticket-price">
								 	€{(ticket.price / 100).toFixed(2)} 
									{!isFree && ticket.price !== 0 && <span className="ticket-fee"> + €{(feePerTicket / 100).toFixed(2)} {t("processingFee")}</span>}
								 </div>
							   </div>
								{(ticket.capacity - ticket.ticketsold !== 0) ? (
									<>
						            <div className="ticket-quantity">
										<select
										  value={quantity}
										  onChange={(e) => handleCountChange(ticket.ticketid, e.target.value)}
										  disabled={eventEnded}
										>
										{Array.from({ length: maxAllowed + 1 }, (_, n) => (
										  <option key={n} value={n}>
										    {n}
										  </option>
										))}
										</select>
									</div>
									<div className="ticket-total-col">
									  <div className="ticket-total">€{lineTotal.toFixed(2)}</div>
									  {quantity > 0 && (
									    <div className="ticket-fee">{quantity} × €{((ticket.price + (ticket.price > 0 ? feePerTicket : 0)) / 100).toFixed(2)}</div>
									  )}
									</div>
									{
										(ticket.capacity - ticket.ticketsold < 6 )? 
										<div className="ticket-price  blink">Only {ticket.capacity - ticket.ticketsold} available !!</div>
										: null 
									}
									
									</>
									) : <div className="ticket-notification-sold"><p>SOLD OUT</p> </div>}
							</div>
					     </div>
		)})}
		</div>
		
		{totalSelected >= MAX_TOTAL_TICKETS && (
		  <div className="ticket-limit-warning">
		    Maximum {MAX_TOTAL_TICKETS} tickets per order.
		  </div>
		)}
		
		{/* Summary Section */}
		<div className="summary-section">
		  {/*<div className="processing-fee">
		    <strong>{t("processingFee")}:</strong> €{(processingfee/100).toFixed(2) || '0.00'}
		  </div>*/}
		  
		  {/* Promo code */}
		  {eventresult?.event?.type !== 'FREE' && (
		    <div className="promo-section">
		      {promoStatus !== 'valid' ? (
		        <div className="promo-input-row">
		          <input
		            type="text"
		            className="promo-input"
		            placeholder="Promo code"
		            value={promoCode}
		            onChange={(e) => { setPromoCode(e.target.value); setPromoStatus(null); setPromoMessage(''); }}
		          />
		          <button type="button" className="promo-apply-btn" onClick={handleApplyPromo}>Apply</button>
		        </div>
		      ) : (
		        <div className="promo-applied-row">
		          <span className="promo-applied-label"> {promoCode.toUpperCase()}</span>
		          <span className="promo-applied-discount">−€{(discount / 100).toFixed(2)}</span>
		          <button type="button" className="promo-remove-btn" onClick={handleRemovePromo}>✕</button>
		        </div>
		      )}
		      {promoMessage && (
		        <p className={`promo-message ${promoStatus === 'valid' ? 'promo-valid' : 'promo-invalid'}`}>
		          {promoMessage}
		        </p>
		      )}
		    </div>
		  )}

		  {promoStatus === 'valid' && (
		    <div className="original-total">
			<strong>{t("total")}:</strong> €{((discountedTotal || grandTotal) / 100).toFixed(2)}
		    </div>
		  )}

		  <div className="grand-total">
		    <strong>{t("total")}:</strong> €{(grandTotal / 100).toFixed(2)}
		  </div>
		</div>
		
		<div className="ticket-form">
		  <button type="submit"  
		  	disabled={ticketsToPurchase.length === 0 || (eventresult?.event?.type !== "FREE" && grandTotal <= 0) || eventEnded} 
			onClick={handleSubmit}>{t("buyTicket")}</button>
		</div>
	
	</>
	

	) : null}

	</div>	

    </div>
	
		
	<div className="ticketpurchase-footer">	
		<div className="footer-copyright">
		<span >© {new Date().getFullYear()} Event Ticketing by Buydeal.eu B.V</span>
		</div>
	
		<div className="lang-dropdown">
		  <select
		    value={i18n.language}
		    onChange={(e) => i18n.changeLanguage(e.target.value)}
		  >
		    {languages.map((lng) => (
		      <option key={lng.code} value={lng.code}>
		        {lng.flag} {lng.label}
		      </option>
		    ))}
		  </select>
		</div>
	</div>	
	</div> //main div

  );
}

export default TicketPurchase;